/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板のチーム共有永続化オーケストレーション。サーバーの保存状態を現在のメンバー行・表示期間へ解決してローカル state に流し込み、編集操作が止まってからデバウンスして全置換保存する。白板文書に未配置のまま残っていた旧データを共有トレイへ引き取り、トレイ保存が確認できてから白板文書から落とす二段階書き戻し（`adoptedTrayBlocks` / `confirmTrayAdoption` / `needsWriteBack`、確認前は未配置のまま持ち回して消失を防ぎ、タブ切替中の遅延完了は `adoptionBoardKeyRef` で取り違えを防ぐ）も担う。リビジョンによる楽観ロック（409 なら取り直し）、保存失敗時の上限付き再試行、白板タブ切替時とアンマウント時の書き出し、ID 連番の衝突回避、保存状態の通知を担う。描画と DnD は PjSandboxView 側に残し、ここは「いつ何を保存し、いつ読み直すか」だけを持つ
 * @graph-connects usePjSandboxBoard [calls] 白板の共有保存状態と編集権限の取得
 * @graph-connects useSavePjSandboxBoard [calls] 白板の全置換保存と操作履歴の追記
 * @graph-connects resolveStoredBoard [calls] 保存モデルから実行時モデルへの解決
 * @graph-connects toStoredBlocks [calls] 実行時ブロックの保存モデルへの変換
 * @graph-connects toStoredCandidates [calls] 実行時候補案件の保存モデルへの変換
 * @graph-connects toStoredTrayBlocks [calls] 引き取り待ちブロックの共有トレイ保存モデルへの変換
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type {
  PjSandboxBoardKey,
  PjSandboxOperationInput,
  PjSandboxStoredBlock,
} from '@cortex/domain-product-innovation-lab-types';
import { usePjSandboxBoard, useSavePjSandboxBoard } from '../../../../hooks/usePjSandbox.js';
import { ApiError } from '../../../../lib/api.js';
import type { PoolWeek } from './constants.js';
import { resolveStoredBoard, toStoredBlocks, toStoredCandidates } from './persistence.js';
import { toStoredTrayBlocks } from './sharedTray.js';
import type { CandidateCase, SandboxBlock, SandboxDay, SandboxMember } from './types.js';

/**
 * 操作が止まってから保存するまでの待ち時間 (ms)。ドラッグ 1 回ごとに書きに行かず、
 * 連続操作を 1 リクエストへまとめる。
 * @graph-connects none
 */
export const AUTOSAVE_DELAY_MS = 1200;

/**
 * 保存が連続して失敗したときに自動再試行を打ち切る回数。無限リトライで API を
 * 叩き続けないための上限で、打ち切り後は保存エラー表示のまま利用者の操作を待つ。
 * @graph-connects none
 */
export const MAX_SAVE_RETRIES = 3;

/** 画面上部に出す保存状態 / 通知。 */
export type SaveNotice =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: string }
  | { kind: 'failed' }
  | { kind: 'forbidden' }
  | { kind: 'conflict'; actor: string };

/**
 * 白板と共有トレイは別ドキュメントとして保存されるため、保存状態も 2 系統になる。
 * 画面には 1 つしか出さないので、利用者にとって切迫している順
 * （失敗・権限・競合 > 保存中 > 保存済み > idle）で 1 つに畳む。
 *
 * 「保存できていない」ことに気づけないのが最悪なので、片方でもエラーならそれを出す。
 * @graph-connects none
 */
export function mergeSaveNotices(primary: SaveNotice, secondary: SaveNotice): SaveNotice {
  const rank = (notice: SaveNotice): number => {
    switch (notice.kind) {
      case 'conflict':
      case 'forbidden':
      case 'failed':
        return 3;
      case 'saving':
        return 2;
      case 'saved':
        return 1;
      default:
        return 0;
    }
  };
  return rank(secondary) > rank(primary) ? secondary : primary;
}

/**
 * 既存 ID の集合から、`prefix-<連番>` 形式の最大連番を求める。
 *
 * 保存された白板を読み込んだあとにブロック / 候補を足すとき、連番を 0 から振り直すと
 * **既存 ID と衝突して別のブロックを上書きしてしまう**。読み込んだ ID の最大連番の次から
 * 振り出すことで、共有された白板に後から足しても ID が重ならない。
 * @graph-connects none
 */
export function maxCounterFrom(ids: string[], prefix: string): number {
  let max = 0;
  for (const id of ids) {
    if (!id.startsWith(`${prefix}-`)) continue;
    const suffix = Number(id.slice(prefix.length + 1));
    if (Number.isInteger(suffix) && suffix > max) max = suffix;
  }
  return max;
}

/**
 * 表示期間の外にあるため描画しないブロックが参照している候補案件の ID を集める。
 *
 * 候補案件の「配置済み」表示は描画中のブロックから導くので、そのままだと**表示期間が
 * 週明けに動いただけで、終わった予定の候補が未着手へ戻って見える**。描画しないぶんも
 * 「白板に置いた」事実は保存に残っているため、ここから拾って表示へ足す。
 * @graph-connects none
 */
function candidateIdsOf(blocks: readonly PjSandboxStoredBlock[]): Set<string> {
  const ids = new Set<string>();
  for (const block of blocks) {
    if (block.sourceCandidateId !== undefined) ids.add(block.sourceCandidateId);
  }
  return ids;
}

interface UsePjSandboxPersistenceInput {
  /** 表示中の白板（チーム）。切り替わったら保存待ちを書き出してから読み直す。 */
  boardKey: PjSandboxBoardKey;
  /** 現在のメンバー行。保存された配置の解決に使う。 */
  members: SandboxMember[];
  /** 横軸の日付。保存された開始日の解決に使う。 */
  days: SandboxDay[];
  /** 候補プールの週セクション。保存された週の解決に使う。 */
  weeks: PoolWeek[];
  /**
   * メンバー行が揃ったか。false のあいだは解決も保存もしない。空の名簿に対して解決すると
   * 配置済みブロックが軒並み「担当者不明」でトレイへ落ち、その状態を保存し返してしまう。
   */
  rosterReady: boolean;
}

interface UsePjSandboxPersistenceResult {
  blocks: SandboxBlock[];
  setBlocks: Dispatch<SetStateAction<SandboxBlock[]>>;
  /**
   * この白板の文書に未配置のまま入っていたため、共有トレイへ引き取るブロック。
   * 呼び出し側が共有トレイへ渡す（白板側の state からは既に外れている）。
   */
  adoptedTrayBlocks: SandboxBlock[];
  /**
   * 引き取ったブロックが共有トレイ文書に入ったことを伝える。これを受けて初めて白板の
   * 文書から落とす（先に落とすと、トレイ側の保存が通らなかったときに案件が消える）。
   */
  confirmTrayAdoption: () => void;
  candidates: CandidateCase[];
  setCandidates: Dispatch<SetStateAction<CandidateCase[]>>;
  /**
   * 表示期間の外にあるため描画しないブロックが参照している候補案件の ID。
   * 候補側の「配置済み」表示に足して、期間が動いただけで未着手へ戻らないようにする。
   */
  carryOverCandidateIds: ReadonlySet<string>;
  /** 閲覧者が白板を編集できるか（サーバー側の判定をそのまま使う）。 */
  canEdit: boolean;
  /** 白板の初回読み込み中か。 */
  loading: boolean;
  /** 現在の保存状態 / 通知。 */
  saveNotice: SaveNotice;
  /** 保存待ちの操作を 1 件積む。積んだ時点で自動保存のデバウンスが動き出す。 */
  recordOp: (op: PjSandboxOperationInput) => void;
  /** 連番のブロック ID を払い出す。 */
  nextBlockId: () => string;
  /** 連番の候補案件 ID を払い出す。 */
  nextCandidateId: () => string;
}

/**
 * PJ Sandbox 白板の共有保存を丸ごと引き受けるフック。
 * @graph-connects none
 */
export function usePjSandboxPersistence(
  input: UsePjSandboxPersistenceInput,
): UsePjSandboxPersistenceResult {
  const { boardKey, members, days, weeks, rosterReady } = input;

  const [blocks, setBlocks] = useState<SandboxBlock[]>([]);
  const [adoptedTrayBlocks, setAdoptedTrayBlocks] = useState<SandboxBlock[]>([]);
  const [candidates, setCandidates] = useState<CandidateCase[]>([]);
  const [carryOverCandidateIds, setCarryOverCandidateIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  // 保存待ちの操作。これが空でない間だけ自動保存が走る（読み込み直後の再保存を防ぐ）。
  const [pendingOps, setPendingOps] = useState<PjSandboxOperationInput[]>([]);
  /**
   * 操作を伴わない保存待ち。共有トレイへ引き取ったブロックを白板の文書から落とすための
   * 書き戻しがこれにあたる（利用者の操作ではないので編集履歴には残さない）。
   */
  const [needsWriteBack, setNeedsWriteBack] = useState(false);
  /** 書き戻しだけが残っているときの再試行トリガー（`pendingOps` の変化では張り直せないため）。 */
  const [saveAttempt, setSaveAttempt] = useState(0);
  const [saveNotice, setSaveNotice] = useState<SaveNotice>({ kind: 'idle' });

  // 表示期間の外にあるため描画していないが、全置換保存で消さないよう持ち回すブロック。
  const carryOverRef = useRef<PjSandboxStoredBlock[]>([]);
  // 楽観ロック用に、読み込んだ（または保存し終えた）時点のリビジョン。
  const revisionRef = useRef(0);
  // 反映済みのサーバー状態を表す署名（`boardKey:revision`）。同じ署名で二重に
  // ハイドレートしない = 自分の保存結果でローカル編集を巻き戻さないための番人。
  const hydratedSignatureRef = useRef<string | null>(null);
  const saveFailureCountRef = useRef(0);
  const blockIdCounter = useRef(0);
  const candidateIdCounter = useRef(0);
  /**
   * いま保存対象になっている白板。タブ切替の瞬間は `boardKey`（新しい白板）より
   * 1 手遅れて更新し、切替前の保存待ちを **元の白板へ** 書き出せるようにする。
   */
  const activeBoardKeyRef = useRef(boardKey);
  /**
   * `activeBoardKeyRef` の白板を解決したときのメンバー行。保存時の位置解決（`rowIndex`
   * → ニックネーム）はこれを使う。タブ切替の瞬間、描画用の `members` は **同じレンダリング内で
   * 新しい白板の名簿へ先に切り替わる**が、そのとき保存対象になっているのは（1 手遅れる
   * `activeBoardKeyRef` が指す）切替前の白板であり、`blocks` の `rowIndex` も切替前の名簿の
   * 並びを指している。ここで描画中の `members` を使うと、旧白板のブロックを新白板の名簿で
   * 解決してしまい、配置情報の欠落や別人の割当が共有ドキュメントへ書き込まれる。解決時の
   * 名簿を保持しておき、常に「その `blocks` を解決したときの名簿」で保存する。
   */
  const activeMembersRef = useRef<SandboxMember[]>(members);
  /**
   * 共有トレイへの引き取り待ちが出ている白板。引き取り済みの通知は非同期に返るため、
   * その間にタブが切り替わっていたら（＝別の白板を保存対象にしていたら）書き戻さない。
   */
  const adoptionBoardKeyRef = useRef<PjSandboxBoardKey | null>(null);

  const boardQuery = usePjSandboxBoard(boardKey);
  const saveBoard = useSavePjSandboxBoard();
  const canEdit = boardQuery.data?.canEdit ?? false;

  // 保存を実行する瞬間の最新値を読むための ref。保存関数自体は安定参照にして、
  // デバウンス用の effect が毎レンダリングで張り直されないようにする。
  const saveStateRef = useRef({
    blocks,
    adoptedTrayBlocks,
    candidates,
    days,
    weeks,
    pendingOps,
    needsWriteBack,
  });
  saveStateRef.current = {
    blocks,
    adoptedTrayBlocks,
    candidates,
    days,
    weeks,
    pendingOps,
    needsWriteBack,
  };

  const nextBlockId = useCallback(() => {
    blockIdCounter.current += 1;
    return `block-${blockIdCounter.current.toString()}`;
  }, []);

  const nextCandidateId = useCallback(() => {
    candidateIdCounter.current += 1;
    return `cand-${candidateIdCounter.current.toString()}`;
  }, []);

  const recordOp = useCallback((op: PjSandboxOperationInput) => {
    setPendingOps((prev) => [...prev, op]);
  }, []);

  /**
   * 引き取ったブロックが共有トレイ文書に収まったので、白板の文書から落とす書き戻しを積む。
   * 引き取り元の白板を表示しているあいだだけ効かせる（タブが変わっていたら、その白板の
   * ブロックはそもそも今の state に無く、書き戻す相手が違う）。
   */
  const confirmTrayAdoption = useCallback(() => {
    if (adoptionBoardKeyRef.current === null) return;
    if (adoptionBoardKeyRef.current !== activeBoardKeyRef.current) return;
    setNeedsWriteBack(true);
  }, []);

  /**
   * 保存待ちの操作をまとめて 1 リクエストで書き込む。操作も書き戻しも無ければ何もしない
   * （読み込み直後に同じ内容を保存し返さないための門番）。
   */
  const flushSave = useCallback(() => {
    const s = saveStateRef.current;
    if (s.pendingOps.length === 0 && !s.needsWriteBack) return;

    const operations = s.pendingOps;
    const writeBack = s.needsWriteBack;
    const targetBoardKey = activeBoardKeyRef.current;
    setPendingOps([]);
    setNeedsWriteBack(false);
    setSaveNotice({ kind: 'saving' });

    // 引き取り待ちのブロックは、共有トレイ側の保存が確認できる（＝ `writeBack`）まで
    // 白板文書に **未配置のまま残す**。引き取ったぶんは読み込み時点で `blocks` から外れて
    // いるため、ここで足さないと利用者の通常操作（`pendingOps`）で走る全置換保存が、
    // トレイ保存の成否に関わらず白板文書から消してしまい、トレイ側が失敗すると両文書とも
    // 案件を失う。確認できた保存でだけ落とす（＝二段階コミットの「落とす」側）。
    const blocks = writeBack
      ? toStoredBlocks({
          blocks: s.blocks,
          members: activeMembersRef.current,
          days: s.days,
          carryOver: carryOverRef.current,
        })
      : toStoredBlocks({
          blocks: s.blocks,
          members: activeMembersRef.current,
          days: s.days,
          carryOver: [...carryOverRef.current, ...toStoredTrayBlocks(s.adoptedTrayBlocks)],
        });

    saveBoard.mutate(
      {
        boardKey: targetBoardKey,
        body: {
          baseRevision: revisionRef.current,
          blocks,
          candidates: toStoredCandidates(s.candidates, s.weeks),
          operations,
        },
      },
      {
        onSuccess: (data) => {
          // この完了が指す白板（`targetBoardKey`）が、いま表示中の白板と違うなら、これは
          // タブ切替後に遅れて返ってきた旧白板ぶんの成功。ここで更新する状態（楽観ロックの
          // `revisionRef`、ハイドレート署名 `hydratedSignatureRef`、引き取り待ち
          // `adoptedTrayBlocks` / `adoptionBoardKeyRef`、保存通知）は **すべて現在表示中の
          // 白板のもの** なので、旧白板の値で踏みつぶすと表示中の白板が壊れる（例: 別白板の
          // revision を送って偽の 409 → 未保存編集の消失）。旧白板は次に開いたとき
          // `revisionRef = 0` から hydrate で読み直すため、ここで取りこぼしは起きない。
          // 個別のミュータブル状態ごとに守衛を足すのではなく、コールバック全体を「保存対象が
          // 表示中の白板と一致するときだけ効かせる」1 つの門で塞ぐ。
          if (targetBoardKey !== activeBoardKeyRef.current) return;

          saveFailureCountRef.current = 0;
          revisionRef.current = data.board.revision;
          // 引き取ったブロックはこの保存で白板の文書から消え、共有トレイだけの持ち物に
          // なった。引き取り待ちを閉じて、以後の再通知で空振りの書き戻しを積まないように
          // する（トレイ側も、取り下げたブロックをこの入力から復活させなくなる）。
          if (writeBack) {
            adoptionBoardKeyRef.current = null;
            setAdoptedTrayBlocks([]);
          }
          // 自分の保存結果は既にローカル state に反映済み。同じ署名を先に記録しておき、
          // ハイドレート effect が走ってもローカルの続きの編集を巻き戻さないようにする。
          hydratedSignatureRef.current = `${targetBoardKey}:${data.board.revision.toString()}`;
          setSaveNotice({
            kind: 'saved',
            at: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
          });
        },
        onError: (error) => {
          // onSuccess と同じ理由で、保存対象（`targetBoardKey`）が表示中の白板と違うなら
          // これはタブ切替後に遅れて返ってきた旧白板ぶんの失敗。以降が触る状態（保存通知・
          // 失敗カウント、409 の `hydratedSignatureRef` / `refetch`、再試行の `pendingOps` /
          // `needsWriteBack`）は **すべて現在表示中の白板のもの** なので、旧白板の失敗で
          // 動かすと表示中の白板が壊れる（例: 旧白板の操作が現在の `pendingOps` に混ざって
          // 編集履歴を汚す、`needsWriteBack` が立って新白板の未確認の引き取り待ちが確認済み
          // 扱いで落ちる、409 の再取得が新白板の未保存編集を巻き戻す）。旧白板は次に開いた
          // とき hydrate で読み直すため、ここで取りこぼしは起きない。onSuccess と同じく
          // コールバック全体を 1 つの門で塞ぐ。
          if (targetBoardKey !== activeBoardKeyRef.current) return;

          const status = error instanceof ApiError ? error.status : 0;
          if (status === 403) {
            setSaveNotice({ kind: 'forbidden' });
            return;
          }
          if (status === 409) {
            // 他の編集者に追い越された。ローカルの続きは破棄し、サーバーの最新を取り直す。
            // 書き戻しは取り直した内容から判定し直すのでここでは積み直さない。
            // 相手の名前は 409 の details に載るが、未保存の白板だと null になりうる。
            const updatedByName =
              error instanceof ApiError ? error.details?.updatedByName : undefined;
            setSaveNotice({
              kind: 'conflict',
              actor: typeof updatedByName === 'string' ? updatedByName : '',
            });
            hydratedSignatureRef.current = null;
            void boardQuery.refetch();
            return;
          }

          saveFailureCountRef.current += 1;
          setSaveNotice({ kind: 'failed' });
          // 一時的な失敗は編集内容を失わないよう積み直して再試行する。
          // ただし連続失敗が続く場合は打ち切り、叩き続けない。
          if (saveFailureCountRef.current <= MAX_SAVE_RETRIES) {
            setPendingOps((prev) => [...operations, ...prev]);
            if (writeBack) setNeedsWriteBack(true);
            // 操作が無い書き戻しだけの保存は `pendingOps` が動かないので、
            // 明示的にデバウンス effect を張り直して再試行させる。
            setSaveAttempt((prev) => prev + 1);
          }
        },
      },
    );
  }, [saveBoard, boardQuery]);

  // 白板タブが切り替わったら、切替前の保存待ちを **元の白板へ** 書き出してから状態を空にする。
  // ハイドレート effect より先に宣言し、同じコミット内で「リセット → 読み込み」の順にする。
  useEffect(() => {
    if (activeBoardKeyRef.current === boardKey) return;
    flushSave();
    activeBoardKeyRef.current = boardKey;
    // 前の白板の内容が一瞬でも新しいタブに見えないよう、明示的に空へ戻す。
    setBlocks([]);
    setAdoptedTrayBlocks([]);
    setCandidates([]);
    setCarryOverCandidateIds(new Set<string>());
    setNeedsWriteBack(false);
    adoptionBoardKeyRef.current = null;
    carryOverRef.current = [];
    revisionRef.current = 0;
    hydratedSignatureRef.current = null;
    saveFailureCountRef.current = 0;
    setSaveNotice({ kind: 'idle' });
  }, [boardKey, flushSave]);

  // サーバーの保存状態を、現在のメンバー行・表示期間・週セクションへ解決して流し込む。
  useEffect(() => {
    const data = boardQuery.data;
    if (data === undefined || !rosterReady) return;
    if (activeBoardKeyRef.current !== boardKey) return;

    const signature = `${boardKey}:${data.board.revision.toString()}`;
    if (hydratedSignatureRef.current === signature) return;
    hydratedSignatureRef.current = signature;

    const resolved = resolveStoredBoard({
      blocks: data.board.blocks,
      candidates: data.board.candidates,
      members,
      days,
      weeks,
      teamKey: boardKey,
    });
    setBlocks(resolved.blocks);
    setCandidates(resolved.candidates);
    // 白板の文書に未配置のまま残っていたブロックは共有トレイの持ち物へ移す。白板側の
    // state からは既に外れているが、文書から落とすのは共有トレイ側の保存が通ってから
    // （`confirmTrayAdoption`）。落とさないままだと次に開いたときも同じブロックを
    // 引き取り続け、共有トレイで消したものが蘇る。
    setAdoptedTrayBlocks(resolved.adoptedTrayBlocks);
    adoptionBoardKeyRef.current = resolved.adoptedTrayBlocks.length > 0 ? boardKey : null;
    carryOverRef.current = resolved.carryOver;
    setCarryOverCandidateIds(candidateIdsOf(resolved.carryOver));
    revisionRef.current = data.board.revision;
    // 保存時の位置解決はこの時点の名簿で行う。以後 `members` が別白板へ切り替わっても、
    // いま解決した `blocks` の `rowIndex` 空間はこの名簿に一致する。
    activeMembersRef.current = members;

    // ID の連番は、読み込んだ白板に既にある最大値の次から振り出す（衝突防止）。
    blockIdCounter.current = maxCounterFrom(
      [...data.board.blocks.map((b) => b.id), ...resolved.carryOver.map((b) => b.id)],
      'block',
    );
    candidateIdCounter.current = maxCounterFrom(
      data.board.candidates.map((c) => c.id),
      'cand',
    );
  }, [boardQuery.data, rosterReady, members, days, weeks, boardKey]);

  // 自動保存: 操作（または書き戻し）が積まれてから AUTOSAVE_DELAY_MS 静かになったら
  // 1 回だけ書きに行く。
  useEffect(() => {
    if (!canEdit) return;
    if (pendingOps.length === 0 && !needsWriteBack) return;
    const timer = setTimeout(flushSave, AUTOSAVE_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [pendingOps, needsWriteBack, saveAttempt, canEdit, flushSave]);

  // 画面を離れるときに保存待ちを取りこぼさないよう、アンマウント時にも書き出す。
  const flushSaveRef = useRef(flushSave);
  flushSaveRef.current = flushSave;
  useEffect(() => {
    return () => {
      flushSaveRef.current();
    };
  }, []);

  return {
    blocks,
    setBlocks,
    adoptedTrayBlocks,
    confirmTrayAdoption,
    candidates,
    setCandidates,
    carryOverCandidateIds,
    canEdit,
    loading: boardQuery.isLoading,
    saveNotice,
    recordOp,
    nextBlockId,
    nextCandidateId,
  };
}
